import { useCallback, useEffect, useMemo, useRef } from "react";

import { useTheme } from "@emotion/react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import vegaEmbed from "vega-embed";

// Maximum data points to display on chart
const AXIS_DATES_MAX = 50;

/**
 * Equipment Debt Service Stacked Bar Chart Component
 * Uses vega-embed directly to create vertical stacked bars showing principal, interest, and extra principal
 * Displays total across all equipment loans
 */
export const EquipmentDebtServiceStackedBarChart = ({ filteredPayments = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const viewRef = useRef(null);

  /**
   * Determine aggregation level based on total unique payment dates
   */
  const getAggregationLevel = useCallback((payments) => {
    // Get unique payment dates
    const uniqueDates = new Set(payments.map((p) => p.payment_date));
    const totalUniqueDates = uniqueDates.size;

    // If more than max allowed, aggregate by year
    if (totalUniqueDates > AXIS_DATES_MAX) {
      return "year";
    }

    // Calculate span in years
    if (payments.length > 0) {
      const firstDate = dayjs(payments[0].payment_date);
      const lastDate = dayjs(payments[payments.length - 1].payment_date);
      const spanYears = lastDate.diff(firstDate, "year", true);

      // For long spans (>10 years), even if under 50 dates, aggregate by year
      if (spanYears > 10) {
        return "year";
      }

      // For medium spans (3-10 years), aggregate by month
      if (spanYears > 3 && totalUniqueDates > 36) {
        return "month";
      }
    }

    // Otherwise show as-scheduled
    return "as-scheduled";
  }, []);

  /**
   * Aggregate payments by time period - returns stacked bar format
   * First aggregates at DATE+LOAN level for as-scheduled, or MONTH+LOAN level for aggregated views
   * to avoid double-counting beginning_balance, then rolls up to display granularity
   */
  const aggregatePayments = useCallback((payments, aggregationLevel) => {
    // Step 1: Aggregate by date or month per loan
    const byLoan = {};

    payments.forEach((payment) => {
      const paymentDate = dayjs(payment.payment_date);
      // For "as-scheduled", use actual date; otherwise use month
      const timeKey =
        aggregationLevel === "as-scheduled" ? paymentDate.format("YYYY-MM-DD") : paymentDate.format("YYYY-MM");
      const loanKey = `${timeKey}_${payment.debt_schedule_id}`;

      if (!byLoan[loanKey]) {
        byLoan[loanKey] = {
          debt_schedule_id: payment.debt_schedule_id,
          time_key: timeKey,
          payment_date: payment.payment_date,
          beginning_balance: parseFloat(payment.beginning_balance || 0),
          principal_total: 0,
          interest_total: 0,
          extra_principal_total: 0,
          payments: [],
        };
      }

      // Take MAX beginning_balance for this loan in this time period (in case of multiple payments)
      const currentBalance = parseFloat(payment.beginning_balance || 0);
      if (currentBalance > byLoan[loanKey].beginning_balance) {
        byLoan[loanKey].beginning_balance = currentBalance;
      }

      // Sum up payment components
      byLoan[loanKey].principal_total += parseFloat(payment.principal_payment || 0);
      byLoan[loanKey].interest_total += parseFloat(payment.interest_payment || 0);
      byLoan[loanKey].extra_principal_total += parseFloat(payment.extra_principal_payment || 0);
      byLoan[loanKey].payments.push(payment);
    });

    // Step 2: Now aggregate to display granularity
    const records = Object.values(byLoan);

    if (aggregationLevel === "as-scheduled") {
      // Group by actual payment_date
      const grouped = {};

      records.forEach((record) => {
        const key = record.time_key; // This is YYYY-MM-DD for as-scheduled

        if (!grouped[key]) {
          grouped[key] = {
            payment_date: record.payment_date, // Use actual payment date
            principal_balance: 0,
            principal_total: 0,
            interest_total: 0,
            extra_principal_total: 0,
            count: 0,
          };
        }

        // Sum across loans (each loan counted once per date)
        grouped[key].principal_balance += record.beginning_balance;
        grouped[key].principal_total += record.principal_total;
        grouped[key].interest_total += record.interest_total;
        grouped[key].extra_principal_total += record.extra_principal_total;
        grouped[key].count += 1;
      });

      // Convert to stacked bar format
      const sortedDates = Object.values(grouped).sort((a, b) => {
        return dayjs(a.payment_date).isBefore(dayjs(b.payment_date)) ? -1 : 1;
      });

      const result = [];
      sortedDates.forEach((group, idx) => {
        const stack = idx + 1;
        result.push(
          {
            payment_date: group.payment_date,
            category: "Principal Balance",
            dataValueY_mean: group.principal_balance,
            stack: stack,
          },
          {
            payment_date: group.payment_date,
            category: "Principal",
            dataValueY_mean: group.principal_total,
            stack: stack,
          },
          {
            payment_date: group.payment_date,
            category: "Interest",
            dataValueY_mean: group.interest_total,
            stack: stack,
          },
          {
            payment_date: group.payment_date,
            category: "Extra Principal",
            dataValueY_mean: group.extra_principal_total,
            stack: stack,
          }
        );
      });

      return result;
    }

    // Group records by display aggregation period
    const grouped = {};

    records.forEach((record) => {
      const paymentDate = dayjs(record.payment_date);
      let key;
      let representativeDate;

      if (aggregationLevel === "year") {
        key = paymentDate.format("YYYY");
        representativeDate = `${key}-01-01`;
      } else if (aggregationLevel === "month") {
        key = record.time_key; // Already YYYY-MM
        representativeDate = `${key}-01`;
      } else if (aggregationLevel === "quarter") {
        const quarter = Math.floor(paymentDate.month() / 3) + 1;
        const year = paymentDate.year();
        key = `${year}-Q${quarter}`;
        // First month of quarter
        const quarterMonth = (quarter - 1) * 3 + 1;
        representativeDate = `${year}-${String(quarterMonth).padStart(2, "0")}-01`;
      }

      // Track unique loans per period to avoid double-counting
      const periodLoanKey = `${key}_${record.debt_schedule_id}`;

      if (!grouped[key]) {
        grouped[key] = {
          payment_date: representativeDate,
          principal_balance: 0,
          principal_total: 0,
          interest_total: 0,
          extra_principal_total: 0,
          period_label: key,
          count: 0,
          loanBalances: {}, // Track each loan's balance separately
        };
      }

      // For each loan in this period, keep the MAX balance
      if (!grouped[key].loanBalances[record.debt_schedule_id]) {
        grouped[key].loanBalances[record.debt_schedule_id] = record.beginning_balance;
      } else {
        grouped[key].loanBalances[record.debt_schedule_id] = Math.max(
          grouped[key].loanBalances[record.debt_schedule_id],
          record.beginning_balance
        );
      }

      // Sum payment components
      grouped[key].principal_total += record.principal_total;
      grouped[key].interest_total += record.interest_total;
      grouped[key].extra_principal_total += record.extra_principal_total;
      grouped[key].count += 1;
    });

    // Sum up loan balances for each period
    Object.keys(grouped).forEach((key) => {
      grouped[key].principal_balance = Object.values(grouped[key].loanBalances).reduce((sum, bal) => sum + bal, 0);
      delete grouped[key].loanBalances; // Clean up temporary tracking
    });

    // Convert to array, sort by date, and transform to stacked bar format
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      return dayjs(a.payment_date).isBefore(dayjs(b.payment_date)) ? -1 : 1;
    });

    const result = [];
    sortedGroups.forEach((group, idx) => {
      const stack = idx + 1;
      result.push(
        {
          payment_date: group.payment_date,
          category: "Principal Balance",
          dataValueY_mean: group.principal_balance,
          stack: stack,
          period_label: group.period_label,
        },
        {
          payment_date: group.payment_date,
          category: "Principal",
          dataValueY_mean: group.principal_total,
          stack: stack,
          period_label: group.period_label,
        },
        {
          payment_date: group.payment_date,
          category: "Interest",
          dataValueY_mean: group.interest_total,
          stack: stack,
          period_label: group.period_label,
        },
        {
          payment_date: group.payment_date,
          category: "Extra Principal",
          dataValueY_mean: group.extra_principal_total,
          stack: stack,
          period_label: group.period_label,
        }
      );
    });

    return result;
  }, []);

  /**
   * Calculate chart data with appropriate aggregation from filtered payments
   */
  const { chartData, aggregationLevel } = useMemo(() => {
    if (!filteredPayments || filteredPayments.length === 0) {
      return { chartData: [], aggregationLevel: "as-scheduled" };
    }

    // Sort by payment_date
    const sortedPayments = [...filteredPayments].sort((a, b) => {
      const dateA = dayjs(a.payment_date);
      const dateB = dayjs(b.payment_date);
      return dateA.isBefore(dateB) ? -1 : 1;
    });

    const level = getAggregationLevel(sortedPayments);

    // Get total aggregated data in stacked bar format
    const totalData = aggregatePayments(sortedPayments, level);

    return { chartData: totalData, aggregationLevel: level };
  }, [filteredPayments, getAggregationLevel, aggregatePayments]);

  // Create Vega-Lite spec for vertical stacked bars
  const vegaSpec = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Calculate today's date and find the next payment date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    // Find the first future payment date or use today
    const futureDates = chartData
      .map((d) => new Date(d.payment_date))
      .filter((d) => d >= today)
      .sort((a, b) => a - b);

    const firstFutureDate = futureDates.length > 0 ? futureDates[0] : today;

    // Always start on the first day of the month
    const startDate = new Date(firstFutureDate);
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    // Filter chart data to only include future payments (from start of month)
    const filteredChartData = chartData.filter((d) => new Date(d.payment_date) >= startDate);

    // Determine X-axis format based on aggregation level
    let xAxisFormat;
    let xAxisLabelAngle = -45;

    if (aggregationLevel === "as-scheduled") {
      // Show full date when not aggregated
      xAxisFormat = "%b %y";
    } else if (aggregationLevel === "month") {
      // Show month and 2-digit year for monthly aggregation
      xAxisFormat = "%b %y";
    } else if (aggregationLevel === "quarter") {
      // Show quarter (approximated as month/year)
      xAxisFormat = "%b %y";
    } else if (aggregationLevel === "year") {
      // Show just year for yearly aggregation
      xAxisFormat = "%Y";
      xAxisLabelAngle = 0; // No angle needed for years
    } else {
      // Default
      xAxisFormat = "%b %y";
    }

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      title: {
        text: "Total Equipment Debt Service",
        color: theme.palette.text.primary,
        offset: 20,
        fontSize: 18,
        fontWeight: 500,
      },
      data: {
        values: filteredChartData,
      },
      config: {
        background: "transparent",
        view: { stroke: null },
        axis: {
          labelFontSize: 14,
          titleFontSize: 16,
        },
      },
      mark: {
        type: "bar",
        cornerRadiusTopLeft: 4,
        cornerRadiusTopRight: 4,
        width: { band: 0.5 },
      },
      encoding: {
        x: {
          field: "payment_date",
          type: "temporal",
          title: "Payment Date",
          axis: {
            format: xAxisFormat,
            labelAngle: xAxisLabelAngle,
            labelFontSize: 14,
            titleFontSize: 16,
            tickCount:
              aggregationLevel === "year"
                ? "year"
                : aggregationLevel === "as-scheduled" || aggregationLevel === "month"
                  ? "month"
                  : undefined,
          },
        },
        y: {
          field: "dataValueY_mean",
          type: "quantitative",
          title: "Amount",
          stack: "zero",
          axis: {
            format: ",.0f",
            formatType: "number",
            labelExpr: "datum.value >= 1000 ? datum.value / 1000 + ' k' : datum.value",
            labelFontSize: 14,
            titleFontSize: 16,
          },
        },
        color: {
          field: "category",
          type: "nominal",
          scale: {
            domain: ["Principal Balance", "Principal", "Interest", "Extra Principal"],
            range: [
              "#B3E5FC", // Principal Balance - Light Light Blue
              theme.palette.primary.main || "#1976d2", // Principal - Blue
              theme.palette.warning.main || "#ed6c02", // Interest - Orange
              theme.palette.success.main || "#2e7d32", // Extra Principal - Green
            ],
          },
          title: "",
          legend: {
            orient: "bottom",
            direction: "horizontal",
            labelFontSize: 14,
            titleFontSize: 16,
          },
        },
        tooltip: [
          { field: "payment_date", type: "temporal", title: "Payment Date", format: "%m %Y" },
          { field: "category", type: "nominal", title: "Category" },
          {
            field: "dataValueY_mean",
            type: "quantitative",
            title: "Value",
            format: ",.2f",
          },
        ],
        opacity: {
          condition: { selection: "hover", value: 0.8 },
          value: 1.0,
        },
      },
      selection: {
        hover: { type: "single", on: "mouseover", empty: "none" },
      },
      width: "container",
      height: 400,
      autosize: { type: "fit", contains: "padding" },
    };
  }, [chartData, aggregationLevel, theme]);

  // Render Vega chart
  useEffect(() => {
    if (!chartRef.current || !vegaSpec) return;

    // Clean up previous view
    if (viewRef.current) {
      viewRef.current.finalize();
      viewRef.current = null;
    }

    vegaEmbed(chartRef.current, vegaSpec, {
      actions: false,
      renderer: "svg",
    })
      .then((result) => {
        viewRef.current = result.view;
      })
      .catch((error) => {
        console.error("Error rendering Vega chart:", error);
      });

    return () => {
      if (viewRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
  }, [vegaSpec]);

  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <Box>
      <div ref={chartRef} style={{ width: "100%" }} />
    </Box>
  );
};
